import * as React from 'react';
// mui
import {
  CardHeader,
  Collapse,
  ListItemButton,
  TextField,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Button,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
// icons
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FlagIcon from '@mui/icons-material/Flag';
import ClearIcon from '@mui/icons-material/Clear';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import DoneIcon from '@mui/icons-material/Done';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
// dayjs
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
// axios
import api from '../../../axios';
// types
import RecurringTask from '../../../interfaces/RecurringTask.interface';
// components
import ItemBreadcrumbs from '../../common/ItemBreadcrumbs';
import { convertEnergyToString, convertPriorityToString, convertTimeToMinutesString } from '../../common/propertyValueToString';
import ItemContentDate from '../../common/ItemContentDate';
import ExcuseDurationSelect from './ExcuseDurationSelect';

interface RecurringTaskCardProps {
  recurringTaskData: RecurringTask;
  onMoveToNextTask: () => void;
}

export default function RecurringTaskCard(props: RecurringTaskCardProps) {
  // props
  const recurringTaskData: RecurringTask = props.recurringTaskData;
  const moveToNextTaskCallback = props.onMoveToNextTask;

  // hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // state
  const [ showExcuseForm, setShowExcuseForm ] = React.useState(false);
  const [ excuseValue, setExcuseValue ] = React.useState<String>('');
  const [ excuseLoading, setExcuseLoading ] = React.useState<boolean>(false);
  const [ completeLoading, setCompleteLoading ] = React.useState<boolean>(false);
  const [ permCompleteLoading, setPermCompleteLoading ] = React.useState<boolean>(false);
  const [ excuseDuration, setExcuseDuration ] = React.useState<string>('');

  const handleExcuseSubmission = () => {
    setExcuseLoading(true);

    api.post('/recurring/excuses/create', {
      content: excuseValue,
      recurringTaskId: recurringTaskData.id,
      date: dayjs.utc().toISOString(),
      duration: excuseDuration,
    })
      .then((res) => {
        console.log(res);
        // reset excuse area in case component is being reloading with new task data
        setShowExcuseForm(false);
        setExcuseValue('');
        
        moveToNextTaskCallback();
      })
      .catch(console.log)
      .finally(() => {
        setExcuseLoading(false);
        setExcuseDuration('');
      })
  }

  const handleComplete = () => {
    setCompleteLoading(true);

    api.put(`/recurring/complete/${recurringTaskData.id}`, {
      date: dayjs().startOf('day').utc().toISOString()
    })
      .then((res) => {
        console.log(res);
        moveToNextTaskCallback();
      })
      .catch(console.log)
      .finally(() => {
        setCompleteLoading(false);
        setExcuseDuration('');
      });
  }

  const handlePermComplete = () => {
    setPermCompleteLoading(true);
    
    api.put(`/recurring/permcomplete/${recurringTaskData.id}`, {
      date: dayjs().startOf('day').utc().toISOString()
    })
      .then((res) => {
        console.log(res);
        moveToNextTaskCallback();
      })
      .catch(console.log)
      .finally(() => {
        setPermCompleteLoading(false);
        setExcuseDuration('');
      });
  }

  const actions = (
    <React.Fragment>
      <Button
        loading={permCompleteLoading}
        onClick={handlePermComplete}
        sx={{ mr: 2, width: isMobile ? '100%' : 'auto', mb: isMobile ? 1 : 0 }}
        variant='outlined'
      >
        <PublishedWithChangesIcon sx={{ pr: 1 }} /> Permanently Complete
      </Button>

      <Button
        variant='contained'
        loading={completeLoading}
        onClick={handleComplete}
        sx={{ width: isMobile ? '100%' : 'auto', mb: isMobile ? 1 : 0 }}
      >
        <DoneIcon sx={{ pr: 1 }} /> Complete
      </Button>
    </React.Fragment>
  )

  return (
    <Card sx={{ width: '100%', m: 0, p: isMobile ? 0 : 2, pt: isMobile ? 0 : 2, minHeight: 'auto', border: isMobile ? 'none' : '' }} variant='outlined'>
      <CardHeader
        title={
          <React.Fragment>
            <RestartAltIcon sx={{
              mb: '-3px',
              mr: '2px'
            }} />{recurringTaskData.title}
          </React.Fragment>
        }
        subheader={
          <React.Fragment>
            {recurringTaskData.dueDate &&
              <ItemContentDate itemData={recurringTaskData} itemType="recurring" />
            }
            <FlagIcon sx={{
              height: '18px',
              width: '20px',
              marginBottom: '-4px',
              ml: recurringTaskData.dueDate ? '4px' : ''
            }} />{convertPriorityToString(recurringTaskData.priority)} 
            <ElectricBoltIcon sx={{
              height: '18px',
              width: '20px',
              marginBottom: '-4px',
              marginLeft: '8px'
            }} />{convertEnergyToString(recurringTaskData.energy)} 
            <AccessAlarmIcon sx={{
              height: '18px',
              width: '20px',
              marginBottom: '-4px',
              marginLeft: '8px'
            }} />{convertTimeToMinutesString(recurringTaskData.time)}
          </React.Fragment>
        }
        action={ isMobile ? null : actions }
      />
      <CardContent sx={{ pt: 0 }}>
        <ItemBreadcrumbs
          currentItem={recurringTaskData}
          itemType="recurring"
          includeCurrentItem={true}
        />

        { recurringTaskData.description && 
          <Typography>{recurringTaskData.description.split('\n').map((line, index) => (
            <React.Fragment key={index}>
              {line}
              <br />
            </React.Fragment>
          ))}<br /></Typography>
        }

        {/* show action buttons here on mobile */}
        { isMobile && actions }

        <ListItemButton onClick={() => setShowExcuseForm(!showExcuseForm)} sx={{ color: 'grey', width: '100%', mt: 2 }}>
          {showExcuseForm ? <KeyboardArrowDownIcon sx={{ pr: 1 }} /> : <KeyboardArrowUpIcon sx={{ pr: 1 }} />} Cant Complete / Don't Want To
        </ListItemButton>
        <Collapse in={showExcuseForm}>
          <TextField
            label="Excuse"
            value={excuseValue}
            onChange={(event) => setExcuseValue(event.target.value)}
            sx={{ paddingBottom: 2, mt: 2 }}
            multiline
            rows={4}
            fullWidth
          />

          <ExcuseDurationSelect
            onChange={(intervalString: string) => setExcuseDuration(intervalString)}
          />

          <Button
            loading={excuseLoading}
            onClick={handleExcuseSubmission}
            disabled={!excuseDuration || !excuseValue}
            variant='outlined'
            fullWidth
          >
            <ClearIcon sx={{ pr: 1}} /> Submit Excuse 
          </Button>
        </Collapse>
      </CardContent>
    </Card>
  );
}